import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import SEO from '@/components/SEO';
import { 
  Eye, 
  Target, 
  Users, 
  Heart, 
  Shield, 
  Zap, 
  Award, 
  Globe, 
  TrendingUp, 
  Clock, 
  Star, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Handshake,
  Leaf,
  Building,
  Truck,
  DollarSign,
  Play,
  Quote,
  Sparkles,
  Rocket,
  Coffee,
  Code,
  Briefcase,
  GraduationCap,
  MessageCircle
} from 'lucide-react';

const stats = [
  { icon: Users, label: 'Active Users', value: '500K+', color: 'from-blue-500 to-cyan-500', description: 'Growing daily' },
  { icon: Building, label: 'Partner Vendors', value: '15K+', color: 'from-green-500 to-emerald-500', description: 'Trusted partners' },
  { icon: MapPin, label: 'Cities Covered', value: '50+', color: 'from-purple-500 to-violet-500', description: 'Across India' },
  { icon: Truck, label: 'Daily Deliveries', value: '25K+', color: 'from-orange-500 to-red-500', description: 'Every single day' },
  { icon: Star, label: 'Customer Rating', value: '4.8/5', color: 'from-yellow-500 to-amber-500', description: 'Customer love' },
  { icon: Clock, label: 'Avg Delivery', value: '12 min', color: 'from-pink-500 to-rose-500', description: 'Lightning fast' }
];

const timeline = [
  {
    year: '2021',
    title: 'The Spark',
    description: 'Founded with a vision to revolutionize local commerce in India. Started in a small garage with big dreams.',
    icon: Lightbulb,
    color: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    achievements: ['Founded by 3 passionate entrepreneurs', 'First prototype developed', 'Seed funding secured']
  },
  {
    year: '2022',
    title: 'First Steps',
    description: 'Launched in Mumbai with 100+ local vendors and our revolutionary 10-minute delivery promise.',
    icon: Rocket,
    color: 'bg-gradient-to-r from-green-500 to-emerald-500',
    achievements: ['Mumbai launch', '100+ vendor partnerships', '1000+ daily orders', 'Mobile app launch']
  },
  {
    year: '2023',
    title: 'Rapid Growth',
    description: 'Expanded to 25 cities across India, partnered with 5K+ local businesses and gained investor confidence.',
    icon: TrendingUp,
    color: 'bg-gradient-to-r from-purple-500 to-violet-500',
    achievements: ['25 cities expansion', '5K+ vendor network', 'Series A funding', '100K+ users']
  },
  {
    year: '2024',
    title: 'Market Leadership',
    description: 'Reached 50+ cities, 15K+ vendors, and 500K+ active users. Became India\'s fastest-growing hyperlocal platform.',
    icon: Award,
    color: 'bg-gradient-to-r from-red-500 to-orange-500',
    achievements: ['50+ cities', '15K+ vendors', '500K+ users', 'Market leader position']
  }
];

const values = [
  {
    icon: Heart,
    title: 'Community First',
    description: 'We prioritize local communities and vendor relationships over profits. Every decision considers community impact.',
    gradient: 'from-red-500 to-pink-500',
    stats: '15K+ vendors supported'
  },
  {
    icon: Shield,
    title: 'Trust & Quality',
    description: 'Every vendor is verified and every product is quality-assured. Your trust is our most valuable asset.',
    gradient: 'from-blue-500 to-cyan-500',
    stats: '99.2% quality rating'
  },
  {
    icon: Zap,
    title: 'Speed & Efficiency',
    description: 'Lightning-fast 10-minute deliveries without compromising quality. Technology meets excellence.',
    gradient: 'from-yellow-500 to-orange-500',
    stats: '12 min avg delivery'
  },
  {
    icon: Handshake,
    title: 'Fair Partnership',
    description: 'Transparent pricing with minimal commission for vendors. We grow together, we succeed together.',
    gradient: 'from-green-500 to-emerald-500',
    stats: '₹50Cr+ vendor earnings'
  },
  {
    icon: Leaf,
    title: 'Sustainability',
    description: 'Eco-friendly packaging and supporting local economies. Good for business, better for the planet.',
    gradient: 'from-purple-500 to-violet-500',
    stats: '100% eco packaging'
  },
  {
    icon: Globe,
    title: 'Innovation',
    description: 'Cutting-edge technology to solve real-world problems. Always pushing boundaries for better solutions.',
    gradient: 'from-indigo-500 to-blue-500',
    stats: '24/7 tech support'
  }
];

const achievements = [
  { number: '2.5M+', label: 'Orders Delivered', icon: CheckCircle },
  { number: '99.2%', label: 'On-Time Delivery', icon: Clock },
  { number: '₹50Cr+', label: 'Vendor Earnings', icon: DollarSign },
  { number: '98%', label: 'Customer Satisfaction', icon: Star }
];

// Responsive orbit configs
const ORBITS = {
  desktop: [
    { radius: 250, dots: 8 }, // outer
    { radius: 180, dots: 8 }, // middle
    { radius: 110, dots: 8 }, // inner
  ],
  tablet: [
    { radius: 180, dots: 8 },
    { radius: 130, dots: 6 },
    { radius: 80, dots: 6 },
  ],
  mobile: [
    { radius: 120, dots: 6 },
    { radius: 85, dots: 6 },
    { radius: 50, dots: 4 },
  ],
};

const teamMembers = [
  { 
    name: 'Arjun Sharma', 
    role: 'CEO & Founder', 
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    bio: 'Former McKinsey consultant with 10+ years in tech. Passionate about local commerce.',
    skills: ['Strategy', 'Leadership', 'Vision'],
    social: { linkedin: '#', twitter: '#' }
  },
  { 
    name: 'Priya Patel', 
    role: 'CTO & Co-Founder', 
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    bio: 'Ex-Google engineer who built hyperlocal delivery algorithms from scratch.',
    skills: ['Technology', 'AI/ML', 'Scalability'],
    social: { linkedin: '#', twitter: '#' }
  },
  { 
    name: 'Rahul Kumar', 
    role: 'Head of Operations', 
    avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
    bio: 'Supply chain expert who optimized delivery networks across 50+ cities.',
    skills: ['Operations', 'Logistics', 'Optimization'],
    social: { linkedin: '#', twitter: '#' }
  },
  { 
    name: 'Sneha Gupta', 
    role: 'Head of Vendor Relations', 
    avatar: 'https://randomuser.me/api/portraits/women/46.jpg',
    bio: 'Community builder who personally onboarded 15K+ local vendors.',
    skills: ['Partnerships', 'Community', 'Growth'],
    social: { linkedin: '#', twitter: '#' }
  },
  { 
    name: 'Vikram Singh', 
    role: 'Product Manager', 
    avatar: 'https://randomuser.me/api/portraits/men/47.jpg',
    bio: 'UX enthusiast focused on creating delightful user experiences.',
    skills: ['Product', 'UX/UI', 'Analytics'],
    social: { linkedin: '#', twitter: '#' }
  },
];

const testimonials = [
  {
    quote: "Dailzy transformed how I run my grocery store. I've tripled my revenue and serve customers I never could reach before.",
    author: "Ramesh Agarwal",
    role: "Local Grocery Store Owner",
    location: "Mumbai",
    avatar: "https://randomuser.me/api/portraits/men/50.jpg"
  },
  {
    quote: "As a working mom, Dailzy is a lifesaver. Fresh vegetables in 10 minutes - it's like having a supermarket next door!",
    author: "Anita Sharma",
    role: "Software Engineer & Mom",
    location: "Bangalore",
    avatar: "https://randomuser.me/api/portraits/women/51.jpg"
  },
  {
    quote: "The team at Dailzy genuinely cares about local businesses. They've helped me digitize my pharmacy and reach more patients.",
    author: "Dr. Suresh Patel",
    role: "Pharmacy Owner",
    location: "Delhi",
    avatar: "https://randomuser.me/api/portraits/men/52.jpg"
  }
];

function getAvatarPositions(breakpoint) {
  if (breakpoint === 'desktop') {
    // Position team members exactly on the orbit circles
    return [
      { orbit: 0, angle: 0 },     // Sarah Johnson: outer, 0° (top)
      { orbit: 0, angle: 135 },   // Michael Chen: outer, 135° (top-left)
      { orbit: 1, angle: 45 },    // Emily Rodriguez: middle, 45° (top-right)
      { orbit: 0, angle: 270 },   // David Kim: outer, 270° (left)
      { orbit: 2, angle: 225 },   // Priya Patel: inner, 225° (bottom-left)
    ];
  } else if (breakpoint === 'tablet') {
    return [
      { orbit: 0, angle: 0 },     // Sarah Johnson: outer, 0° (top)
      { orbit: 0, angle: 90 },    // Michael Chen: outer, 90° (right)
      { orbit: 1, angle: 45 },    // Emily Rodriguez: middle, 45° (top-right)
      { orbit: 1, angle: 225 },   // David Kim: middle, 225° (bottom-left)
      { orbit: 2, angle: 180 },   // Priya Patel: inner, 180° (bottom)
    ];
  } else {
    // mobile
    return [
      { orbit: 0, angle: 0 },     // Sarah Johnson: outer, 0° (top)
      { orbit: 0, angle: 120 },   // Michael Chen: outer, 120° (bottom-right)
      { orbit: 1, angle: 60 },    // Emily Rodriguez: middle, 60° (top-right)
      { orbit: 1, angle: 240 },   // David Kim: middle, 240° (bottom-left)
      { orbit: 2, angle: 180 },   // Priya Patel: inner, 180° (bottom)
    ];
  }
}

// Helper to get current breakpoint
function useBreakpoint() {
  const [bp, setBp] = React.useState('desktop');
  React.useEffect(() => {
    function check() {
      if (window.innerWidth < 640) setBp('mobile');
      else if (window.innerWidth < 1024) setBp('tablet');
      else setBp('desktop');
    }
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return bp;
}

const CountUp = ({ end, duration = 2000 }: { end: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
      let startTimestamp: number;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, hasAnimated, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
};

const About = () => {
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Dailzy",
      "description": "India's fastest-growing hyperlocal quick commerce platform revolutionizing how people shop for daily essentials and luxury goods",
      "foundingDate": "2021",
      "founders": [
        {
          "@type": "Person",
          "name": "Arjun Sharma",
          "jobTitle": "CEO & Founder"
        },
        {
          "@type": "Person", 
          "name": "Priya Patel",
          "jobTitle": "CTO & Co-Founder"
        }
      ],
      "numberOfEmployees": "500+",
      "location": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN",
          "addressRegion": "India"
        }
      },
      "knowsAbout": ["Quick Commerce", "Hyperlocal Delivery", "Local Business", "Technology", "Logistics"],
      "award": ["Fastest Growing Startup 2024", "Best Local Commerce Platform"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Quick Commerce Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Grocery Delivery"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Electronics Delivery"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Medicine Delivery"
            }
          }
        ]
      }
    }
  };

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const breakpoint = useBreakpoint();
  const orbits = ORBITS[breakpoint];
  const avatarPositions = getAvatarPositions(breakpoint);
  const viewBox = breakpoint === 'desktop' ? 600 : breakpoint === 'tablet' ? 400 : 300;
  const center = viewBox / 2;

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="About Dailzy - India's Fastest Growing Quick Commerce Platform | Our Story"
        description="Learn about Dailzy's journey from a startup to India's leading quick commerce platform. Discover our mission to revolutionize local commerce with 10-minute delivery across 50+ cities."
        keywords="about dailzy, quick commerce company, hyperlocal delivery startup, indian ecommerce, local business platform, fast delivery service, dailzy story, company history"
        url="https://dailzy.com/about"
        type="website"
        structuredData={aboutStructuredData}
      />
      {/* Enhanced Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-white via-red-50 to-orange-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-40 right-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-20 left-40 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <motion.div 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-red-100 to-orange-100 text-red-600 rounded-2xl px-6 py-3 mb-8 shadow-lg"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles size={20} className="animate-pulse" />
              <span className="font-bold text-lg">Our Story</span>
            </motion.div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              Revolutionizing{" "}
              <span className="relative">
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  Local Commerce
                </span>
                <motion.div
                  className="absolute -bottom-4 left-0 right-0 h-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </span>
            </h1>
            
            <motion.p 
              className="text-2xl text-gray-600 mb-12 leading-relaxed max-w-4xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Connecting communities with their neighborhood vendors through technology, transparency, and trust. 
              <span className="font-semibold text-gray-800"> Building the future of hyperlocal commerce.</span>
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Button 
                size="lg"
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-10 py-4 rounded-xl text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <Play className="mr-3 w-5 h-5" />
                Watch Our Story
              </Button>
              <Button 
                size="lg"
                variant="outline" 
                className="border-2 border-red-300 text-red-600 hover:bg-red-50 px-10 py-4 rounded-xl text-lg font-semibold transition-all duration-300"
              >
                <Coffee className="mr-3 w-5 h-5" />
                Meet the Team
              </Button>
            </motion.div>

            {/* Quick Stats Preview */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              {stats.slice(0, 4).map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                  <div className="text-3xl font-bold text-gray-800 mb-1">
                    <CountUp end={parseInt(stat.value.replace(/[^\d]/g, ''))} />
                    {stat.value.includes('+') && '+'}
                    {stat.value.includes('/') && '/5'}
                  </div>
                  <div className="text-sm font-medium text-gray-600">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section className="py-24 bg-white relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold text-gray-800 mb-6">
              Numbers That <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Matter</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our impact measured in real numbers, real growth, and real community transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="group relative overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="relative p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 group-hover:border-gray-200">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <stat.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-800 mb-2">{stat.value}</div>
                    <div className="text-lg font-semibold text-gray-700 mb-2">{stat.label}</div>
                    <div className="text-sm text-gray-500">{stat.description}</div>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Vision & Mission Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-40 left-20 w-96 h-96 bg-red-100 rounded-full mix-blend-multiply filter blur-2xl opacity-50"></div>
          <div className="absolute bottom-40 right-20 w-96 h-96 bg-orange-100 rounded-full mix-blend-multiply filter blur-2xl opacity-50"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-red-100 to-orange-100 text-red-600 rounded-2xl px-6 py-3 mb-8">
              <Target size={20} />
              <span className="font-bold text-lg">Our Foundation</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-8">
              Vision & <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Every great company starts with a purpose. Ours is to revolutionize local commerce by creating meaningful connections between communities and their trusted neighborhood vendors.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto mb-20">
            <motion.div 
              className="relative group"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl rotate-2 group-hover:rotate-3 transition-transform duration-500 opacity-10"></div>
              <div className="relative bg-white rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-gray-100">
                <div className="flex items-center gap-6 mb-8">
                  <div className="bg-gradient-to-br from-red-500 to-red-600 w-24 h-24 rounded-3xl flex items-center justify-center shadow-xl">
                    <Eye className="w-12 h-12 text-white" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold text-gray-900 mb-2">Our Vision</h3>
                    <p className="text-red-500 font-bold text-lg">What We See</p>
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  To become India's most trusted hyperlocal commerce platform that empowers every neighborhood vendor to thrive in the digital economy while providing customers with instant access to quality products from their local community.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold">Hyperlocal Focus</span>
                  <span className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold">Vendor Empowerment</span>
                  <span className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold">Community Building</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="relative group"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl -rotate-2 group-hover:-rotate-3 transition-transform duration-500 opacity-10"></div>
              <div className="relative bg-white rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-gray-100">
                <div className="flex items-center gap-6 mb-8">
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 w-24 h-24 rounded-3xl flex items-center justify-center shadow-xl">
                    <Target className="w-12 h-12 text-white" />
                  </div>
                  <div>
                    <h3 className="text-4xl font-bold text-gray-900 mb-2">Our Mission</h3>
                    <p className="text-orange-500 font-bold text-lg">What We Do</p>
                  </div>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  To bridge the gap between traditional local commerce and modern convenience through innovative technology, transparent operations, and genuine partnerships that benefit both vendors and customers in building stronger communities.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">Technology Bridge</span>
                  <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">Transparent Operations</span>
                  <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold">Fair Partnerships</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Impact Statement */}
          <motion.div 
            className="text-center max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl p-12 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10 rounded-3xl"></div>
              <div className="relative z-10">
                <Quote className="w-16 h-16 mx-auto mb-6 opacity-50" />
                <h3 className="text-4xl font-bold mb-8">Why This Matters</h3>
                <p className="text-xl leading-relaxed mb-12 opacity-95 max-w-4xl mx-auto">
                  In an era of global commerce, we believe in the power of local. Every purchase on Dailzy supports a neighborhood vendor, strengthens community bonds, and creates a more sustainable economic ecosystem. We're not just delivering products—we're delivering prosperity to local businesses and convenience to communities.
                </p>
                <div className="grid md:grid-cols-3 gap-8 text-center">
                  <div className="p-6 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="text-5xl font-bold mb-3">15K+</div>
                    <div className="text-red-100 text-lg">Local Vendors Empowered</div>
                  </div>
                  <div className="p-6 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="text-5xl font-bold mb-3">₹50Cr+</div>
                    <div className="text-red-100 text-lg">Earnings Generated</div>
                  </div>
                  <div className="p-6 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <div className="text-5xl font-bold mb-3">50+</div>
                    <div className="text-red-100 text-lg">Communities Connected</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Core Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
              Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The principles that guide every decision we make and shape our culture. These aren't just words on a wall—they're lived every day.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                className="group relative overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="relative p-8 bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-gray-200 h-full">
                  <div className={`w-20 h-20 mb-8 rounded-3xl bg-gradient-to-br ${value.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">{value.description}</p>
                  <div className="text-sm font-semibold text-gray-500 bg-gray-50 px-4 py-2 rounded-full inline-block">
                    {value.stats}
                  </div>
                  
                  {/* Hover Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.gradient} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Timeline Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
              Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Journey</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              From a simple idea in a garage to India's fastest-growing hyperlocal commerce platform. Every milestone tells a story of perseverance, innovation, and community impact.
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div 
                key={index}
                className="relative flex items-start mb-16 last:mb-0"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Timeline Line */}
                {index < timeline.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-32 bg-gradient-to-b from-gray-300 to-transparent"></div>
                )}
                
                {/* Year Badge */}
                <div className="flex-shrink-0 mr-8">
                  <div className={`w-16 h-16 rounded-2xl ${item.color} flex items-center justify-center shadow-lg`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center mt-2">
                    <div className="text-sm font-bold text-gray-600">{item.year}</div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{item.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed mb-6">{item.description}</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {item.achievements.map((achievement, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700 font-medium">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Team Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
              Meet Our <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The passionate individuals behind Dailzy's success. Each bringing unique expertise and shared vision for revolutionizing local commerce.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <motion.div 
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 text-center relative overflow-hidden">
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                  
                  <div className="relative z-10">
                    <div className="relative mb-6">
                      <img 
                        src={member.avatar} 
                        alt={member.name}
                        className="w-24 h-24 rounded-full mx-auto shadow-lg group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-red-500 font-semibold mb-4">{member.role}</p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-6">{member.bio}</p>
                    
                    <div className="flex flex-wrap gap-2 justify-center mb-6">
                      {member.skills.map((skill, i) => (
                        <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="group-hover:bg-red-500 group-hover:text-white group-hover:border-red-500 transition-all duration-300"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Connect
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6">
              What People <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Real stories from real people whose lives have been transformed by Dailzy
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="bg-white rounded-3xl p-12 shadow-2xl text-center relative"
              >
                <Quote className="w-16 h-16 text-red-200 mx-auto mb-8" />
                <p className="text-2xl text-gray-700 leading-relaxed mb-8 italic">
                  "{testimonials[activeTestimonial].quote}"
                </p>
                <div className="flex items-center justify-center gap-4">
                  <img 
                    src={testimonials[activeTestimonial].avatar} 
                    alt={testimonials[activeTestimonial].author}
                    className="w-16 h-16 rounded-full shadow-lg"
                  />
                  <div className="text-left">
                    <div className="font-bold text-gray-900 text-lg">{testimonials[activeTestimonial].author}</div>
                    <div className="text-red-500 font-medium">{testimonials[activeTestimonial].role}</div>
                    <div className="text-gray-500 text-sm">{testimonials[activeTestimonial].location}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial 
                      ? 'bg-red-500 w-8' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-red-500 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-8">
              Join Our Mission
            </h2>
            <p className="text-2xl text-red-100 mb-12 max-w-4xl mx-auto leading-relaxed">
              Be part of the revolution that's transforming local commerce across India. 
              Whether you're a vendor, customer, or passionate professional, there's a place for you at Dailzy.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                size="lg"
                className="bg-white text-red-500 hover:bg-gray-100 px-10 py-4 rounded-xl text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <Briefcase className="mr-3 w-5 h-5" />
                Join Our Team
              </Button>
              <Button 
                size="lg"
                variant="outline" 
                className="border-2 border-white bg-transparent px-10 py-4 rounded-xl text-lg font-bold transition-all duration-300 text-white hover:text-red-500 hover:bg-white group"
              >
                <Building className="mr-3 w-5 h-5 text-white group-hover:text-red-500" />
                <span className="text-white group-hover:text-red-500">Become a Partner</span>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 